import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useGameConfig } from '@pp/core/src/config/GameConfigProvider';
import { DeviceInfo } from '@pp/core/src/deviceInfo/deviceInfo';
import { useCoreDispatch } from '@pp/core/src/store/CoreStore';
import { useConfigSlice } from '@pp/core/src/store/slices/config/selectors';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { gameStateActions } from '@pp/core/src/store/slices/game/slice';

import { CrashTrafficLight, ICrashTrafficLightStatus } from './CrashTrafficLight';
import { baseTrafficLightConfig } from '@pp/core/src/components/game/trafficLight/types';

interface TrafficLightProps {
  line?: boolean;
  floatingStatusBar?: boolean;
}

export const CrashTrafficLightConnected: FC<TrafficLightProps> = ({ line, floatingStatusBar = false }) => {
  const dispatch = useCoreDispatch();
  const timer = useGameStateSlice.getTimer(); // in seconds
  const timerStartTime = useGameStateSlice.getTimerStartTime(); // 1701970321681 | Date.now()
  const statusName = useGameStateSlice.getCurrentStatus().toUpperCase();
  const config =  useGameConfig().config?.trafficLightConfig ?? baseTrafficLightConfig;
  const status =  config[statusName];
  const timings = useConfigSlice.getTimings();
  const isIosChrome = useMemo(() => DeviceInfo.getCustomFlags()?.IS_IOS_CHROME, []);

  const [animationDuration, setAnimationDuration] = useState<number>(0); // in ms
  const progressbarRef = useRef<HTMLDivElement>(null);
  const [startingTime, setStartingTime] = useState(timerStartTime);

  const onScheduledStatusChange = useCallback(
    (nextStatus: string) => {
      dispatch(gameStateActions.setCurrentGameStatus(nextStatus));
    },
    [dispatch]
  );

  const startTimer = useCallback(
    (timer: number, timerStartTime: number): void => {
      const startingTime = Date.now();
      const elapsedMilliSeconds = startingTime - timerStartTime;
      const timerInMilliseconds = timer * 1_000; //9 -> 9000
      const calculatedDuration = timerInMilliseconds - elapsedMilliSeconds;
      if (timings) {
        setAnimationDuration(calculatedDuration);
        setStartingTime(startingTime);
      }
    },
    [timings]
  );

  const statusWithTiming = useMemo((): ICrashTrafficLightStatus => {
    if (!status.scheduledStatusChange || !timings) {
      return status;
    } else if (typeof status.scheduledStatusChange.timeout === 'number') {
      return {
        ...status,
        calculatedScheduledStatusChange: {
          timeout: status.scheduledStatusChange.timeout,
          nextStatus: status.scheduledStatusChange.nextStatus,
        },
      };
    } else if (typeof status.scheduledStatusChange.timeout === 'function') {
      return {
        ...status,
        calculatedScheduledStatusChange: {
          ...status.scheduledStatusChange,
          timeout: status.scheduledStatusChange.timeout(timings),
        },
      };
    } else {
      return {
        ...status,
        calculatedScheduledStatusChange: {
          ...status.scheduledStatusChange,
          timeout: timings[status.scheduledStatusChange.timeout],
        },
      };
    }
  }, [status, timings]);

  useEffect(() => {
    startTimer(timer, timerStartTime);
  }, [timer, timerStartTime, startTimer]);

  return (
    <CrashTrafficLight
      animationDuration={animationDuration}
      fallbackAnimation={isIosChrome}
      onScheduledStatusChange={onScheduledStatusChange}
      ref={progressbarRef}
      status={statusWithTiming}
      timerStartTime={startingTime}
      resetAnimationDurationCallback={() => {
        setAnimationDuration(0);
      }}
      line={line}
      floatingStatusBar={floatingStatusBar}
    />
  );
};
